import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./index.module.less";
import { MessageSendType } from "@/types/chat/enum";
import {
  buildQuickReplyMessageBody,
  convertEmployeeToString,
  isEqualEmployee,
  openQuickReplyManual,
  QUICK_REPLY_TIP_KEY,
  sleep
} from "@/utils/chat/message";
import ChatState from "@/types/chat/State";
import { connect } from "dva";
import { getClientType } from "@/utils";
import { ClientType } from "@/types/chat";
import ImService from "@/server/ImService";
import Bus from "@/utils/bus";
import ChatContext from "@/components/chat/context";
import { QuickReplyStatus } from "@jd/jdee.im.sdk/lib/es/protocol/message/Text";
import Text from "@/components/chat/message/Text";
import { getTextMessageContent } from "@/components/chat/utils/commonMessageModal";
import { Avatar } from "@/baseComponents/Avatar";
import { getHeadImgThumbUrl, compareUserProp, compareProp } from "@/utils/chat/index";
import IconFont from "@/components/icon";
import { Button, message as toast } from "antd";
import { MessageExtAction } from "@jd/jdee.im.sdk/lib/es/protocol/messageExtend/Type";
import QuickReplyDetail from "./QuickReplyDetail";
import { groupBy } from "lodash";
import { nyrsf } from "@/utils/date";
import { syncConfirmNew } from "@/utils/syncConfirm";
import ReplyMessage from "@/components/chat/message/ReplyMessage";
import { changeEgovUrl } from "@/utils/tools";
import { UserCardType } from "@/types/UserCard";

/**
 * 快捷回复
 * @returns
 */
function QuickReplyCard(props: any) {
  const { quickReplyConfig, userQuickReply, isSmall, lastMessage } = React.useContext(ChatContext);
  const { sendType, message, selectedSession, currentEmployee, notShowReply, children } = props;
  const [replyDetail, setReplyDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const isOthers = useMemo(() => {
    return sendType === MessageSendType.OTHER;
  }, [sendType]);
  // 超时
  const overtime = useMemo(() => {
    const time = (message?.timestamp || 0) + (quickReplyConfig?.duration || 0);
    return time && new Date().getTime() > time;
  }, [message.timestamp, quickReplyConfig?.duration]);

  // 回复
  const handleQuickReply = useCallback(() => {
    // 发送快速回复
    setLoading(true);
    const msg = buildQuickReplyMessageBody({
      mid: message.mid,
      session: selectedSession,
      msgContentItem: {
        users: [
          {
            pin: currentEmployee.userId,
            app: currentEmployee.app,
            teamId: currentEmployee.teamId,
            clientType: getClientType() === "MAC" ? ClientType.MAC : ClientType.PC,
            timeStamp: new Date().getTime()
          }
        ]
      },
      action: MessageExtAction.ADD
    });
    if (msg) {
      ImService.getInstance()
        ?.sendChatMessageExt(selectedSession.sessionId, msg)
        .finally(() => {
          toast.success({
            key: QUICK_REPLY_TIP_KEY,
            content: "已回复"
          });
          setLoading(false);
        });
    }
  }, [message.mid, selectedSession, currentEmployee]);

  // 撤回回复
  const handleRevokeReply = useCallback(() => {
    const msg = buildQuickReplyMessageBody({
      mid: message.mid,
      session: selectedSession,
      msgContentItem: {
        users: [
          {
            pin: currentEmployee.userId,
            app: currentEmployee.app,
            teamId: currentEmployee.teamId,
            clientType: getClientType() === "MAC" ? ClientType.MAC : ClientType.PC,
            timeStamp: new Date().getTime()
          }
        ]
      },
      action: MessageExtAction.DELETE
    });
    if (msg) {
      ImService.getInstance()
        ?.sendChatMessageExt(selectedSession.sessionId, msg)
        .finally(() => {
          toast.success({
            key: QUICK_REPLY_TIP_KEY,
            content: "已撤回回复"
          });
          setLoading(false);
        });
    }
  }, [
    currentEmployee.app,
    currentEmployee.teamId,
    currentEmployee.userId,
    message.mid,
    selectedSession
  ]);

  // 关闭/开启
  const changeQuickReplyStatus = useCallback(
    async (status: number) => {
      if (overtime) {
        return;
      }
      // 发送快速回复
      if (status === QuickReplyStatus.CLOSE) {
        const res = await syncConfirmNew(
          "提醒：关闭回复后，期间所有群成员将无法再进行回复，直到您重新发起回复。",
          "确定要关闭回复吗？"
        );
        if (!res) {
          return;
        }
      }
      openQuickReplyManual({
        message,
        selectedSession,
        status
      });
    },
    [message, selectedSession, overtime]
  );

  const openUserCard = useCallback((user: any) => {
    Bus.emit("app:toggle-user-card-modal:show", {
      visible: true,
      type: UserCardType.User,
      userId: user.userId,
      teamId: user.teamId,
      appId: user.app
    });
  }, []);

  const replyUsers = useMemo(() => {
    if (!message?.msgContent?.quickReplyUsers?.length) {
      return [];
    }
    const list = [...message.msgContent.quickReplyUsers];
    const users = list.sort(compareUserProp("timeStamp", currentEmployee));
    return users || [];
  }, [currentEmployee, message.msgContent]);

  const replyUserTimes = useMemo(() => {
    if (!message?.msgContent?.quickReplyUsers?.length) {
      return [];
    }
    const list = [...message.msgContent.quickReplyUsers];
    const users = list.sort(compareProp("timeStamp"));
    return users || [];
  }, [message.msgContent]);

  // 人员数
  const userNum = useMemo(() => {
    return replyUsers?.length || 0;
  }, [replyUsers.length]);

  // 单位数
  const unitNum = useMemo(() => {
    const group = groupBy(replyUsers, "unitDeptId");
    return Object.keys(group).length;
  }, [replyUsers]);

  const hasReply = useMemo(() => {
    if (!replyUsers?.length) {
      return false;
    }
    const idx = replyUsers.findIndex((i: any) => {
      return convertEmployeeToString(i) === convertEmployeeToString(currentEmployee);
    });
    return idx > -1;
  }, [currentEmployee, replyUsers]);

  // 开启或开启过
  const hasOpened = useMemo(() => {
    return (
      message?.quickReplyStatus === QuickReplyStatus.OPEN ||
      message?.quickReplyStatus !== QuickReplyStatus.UNOPEN
    );
  }, [message.quickReplyStatus]);

  const hasOpen = useMemo(() => {
    return message?.quickReplyStatus === QuickReplyStatus.OPEN;
  }, [message.quickReplyStatus]);

  const unOpen = useMemo(() => {
    return message?.quickReplyStatus === QuickReplyStatus.UNOPEN;
  }, [message.quickReplyStatus]);

  // 发起人操作
  const ownerAction = useMemo(() => {
    if (overtime) {
      return <div className={`${styles["btn"]} ${styles["disable"]}`}>已关闭回复</div>;
    }
    return !hasOpen ? (
      <div className={`${styles["btn"]}`} onClick={() => changeQuickReplyStatus(1)}>
        {`${!unOpen ? "重新" : ""}发起回复`}
      </div>
    ) : (
      <div className={styles["btn"]} onClick={() => changeQuickReplyStatus(0)}>
        关闭回复
      </div>
    );
  }, [changeQuickReplyStatus, hasOpen, overtime, unOpen]);

  const needOtherReply = useMemo(() => {
    return isOthers && !(overtime || (hasOpened && !hasOpen)) && !hasReply;
  }, [hasOpen, hasOpened, hasReply, isOthers, overtime]);

  // 收取人操作
  const otherAction = useMemo(() => {
    return overtime || (hasOpened && !hasOpen) ? (
      <div className={`${styles["text-wrap"]} ${styles["disable"]}`}>
        <span className={styles["text"]}>已关闭回复</span>
      </div>
    ) : hasReply ? (
      <div className={`${styles["text-wrap"]} ${styles["success"]}`}>
        <IconFont type="iconjs_ic_success" className={styles["icon"]} />
        <span className={styles["text"]}>已回复</span>
      </div>
    ) : (
      <>
        <span className={styles["footer-tip"]}>请您直接点击按钮回复</span>
        <Button
          type="primary"
          disabled={loading || hasReply || !hasOpen}
          onClick={handleQuickReply}
        >
          {quickReplyConfig?.replyText || "收到"}
        </Button>
      </>
    );
  }, [
    handleQuickReply,
    hasOpen,
    hasOpened,
    hasReply,
    loading,
    overtime,
    quickReplyConfig?.replyText
  ]);

  const isForbiddenQuickReply = useMemo(() => {
    return quickReplyConfig?.trigger === 0;
  }, [quickReplyConfig?.trigger]);

  const notReplay = useMemo(() => {
    return (
      (!hasOpened && isOthers) ||
      // 发起方关闭回复设置
      (!hasOpened && !isOthers && userQuickReply === 0) ||
      // 超时
      (!hasOpened && overtime)
    );
  }, [hasOpened, isOthers, overtime, userQuickReply]);

  // 截止时间
  const deadline = useMemo(() => {
    const time = (message?.timestamp || 0) + (quickReplyConfig?.duration || 0);
    if (!time) {
      return "";
    }
    return nyrsf(time);
  }, [message.timestamp, quickReplyConfig?.duration]);

  const infoComp = useMemo(() => {
    return (
      <>
        <span className={styles["item"]}>
          已回复：<span className={styles["num"]}>{userNum}</span>人
        </span>
        <span className={styles["item"]}>
          已回复单位：<span className={styles["num"]}>{unitNum}</span>个
        </span>
      </>
    );
  }, [unitNum, userNum]);

  const replyComp = useMemo(() => {
    return (
      <div className={styles["reply-wrap"]}>
        {replyUsers?.length ? (
          <div className={styles["users-wrap"]}>
            {replyUsers.slice(0, 8).map((user: any) => {
              return (
                <div
                  className={styles["item"]}
                  key={`reply_${convertEmployeeToString(user)}`}
                  onClick={() => openUserCard(user)}
                >
                  <Avatar
                    avatarMask={false}
                    size={7}
                    className={styles["avatar"]}
                    name={user.name}
                    src={changeEgovUrl(getHeadImgThumbUrl(user.avatar, 150, 150))}
                    styleObj={{borderRadius: "50%"}}
                  />
                  <span className={styles["name"]}>{user.name}</span>
                </div>
              );
            })}
          </div>
        ) : null}
        <div
          className={`${styles["info-wrap"]} cursor-pointer`}
          onClick={() => setReplyDetail(true)}
        >
          {infoComp}
          <span className={`${styles["item"]}`}>
            查看全部
            <IconFont type="iconic_pc_LineRight" className={styles["icon"]} />
          </span>
        </div>
        <span className={styles["tip"]}>截止时间：{deadline}</span>
      </div>
    );
  }, [replyUsers, infoComp, deadline, openUserCard]);

  const isSelf = useMemo(() => {
    return isEqualEmployee(message.sender, currentEmployee);
  }, [currentEmployee, message?.sender]);

  useEffect(() => {
    if (lastMessage?.id === message.id && !isOthers) {
      sleep(60).then(() => {
        wrapRef?.current?.scrollIntoView({
          block: "end"
        });
      });
    }
  }, [isOthers, lastMessage.id, message.id]);
  return notReplay || (!hasOpened && isForbiddenQuickReply) ? (
    <Text {...props} />
  ) : (
    <>
      <div
        ref={wrapRef}
        className={`${styles["quick-reply-card-wrap"]} ${
          !isOthers ? styles["owner"] : styles["other"]
        } ${hasOpened ? styles["open"] : styles["close"]} ${isSmall ? styles["small"] : ""}`}
      >
        {children}
        {hasOpened && replyComp}
        <div className={`${styles["footer"]} ${needOtherReply ? styles["reply"] : ""}`}>
          {!isOthers ? ownerAction : otherAction}
        </div>
        {!notShowReply && selectedSession.isGroup && message?.tag === 1 && (
          <IconFont
            className={`${styles["tag-icon"]} ${isSelf ? styles["mine"] : styles["others"]}`}
            type={!isSelf ? "iconic_app_mark_solid_left" : "iconic_app_mark_solid_right"}
          />
        )}
      </div>
      {replyDetail && (
        <QuickReplyDetail
          detailVisible={replyDetail}
          handleClose={() => setReplyDetail(false)}
          replyUsers={replyUserTimes}
          userNum={userNum}
          unitNum={unitNum}
          hasOpen={hasOpen}
          unOpen={unOpen}
          hasReply={hasReply}
          isOwner={!isOthers}
          overtime={overtime}
          handleRevokeReply={handleRevokeReply}
          changeQuickReplyStatus={changeQuickReplyStatus}
          openUserCard={openUserCard}
        />
      )}
    </>
  );
}

function mapStateToProps({ chat }: { chat: ChatState }) {
  const { selectedSession, currentEmployee } = chat;
  return {
    selectedSession,
    currentEmployee
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateSessionMessageMid(data: { sendResult: any; sessionId: string }) {
      dispatch({ type: "chat/updateSessionMessageMid", payload: data });
    }
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(QuickReplyCard);
